import { useState, useRef, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useChat } from '@/hooks/use-chat';
import { useAuth } from '@/hooks/use-auth';
import { Send, Image, Paperclip, Loader2 } from 'lucide-react';
import type { ChatMessage, ChatParticipant, Chat } from '@/lib/types/chat';

interface ChatWindowProps {
  chat: Chat;
  messages: ChatMessage[];
  onSendMessage: (content: string) => void;
  onSendAttachment: (file: File) => Promise<void>;
  conversationId?: string;
  title?: string;
  participants?: ChatParticipant[];
}

export function ChatWindow({ chat, messages, onSendMessage, onSendAttachment, conversationId, title, participants }: ChatWindowProps) {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const typingTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;

    onSendMessage(message);
    setMessage('');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
    setIsTyping(true);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Set new timeout
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onSendAttachment(file);
  };

  return (
    <Card className="flex flex-col h-[600px] rounded-none border-0">
      {/* Header */}
      <div className="border-b p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <h2 className="font-semibold">{title || chat.title}</h2>
            <div className={`w-2 h-2 rounded-full bg-green-500`} />
          </div>
          <div className="flex -space-x-2">
            {(participants || chat.participants)?.slice(0, 3).map((participant) => (
              <Avatar key={participant.id} className="border-2 border-background">
                <AvatarImage src={participant.avatar} />
                <AvatarFallback>{participant.name[0]}</AvatarFallback>
              </Avatar>
            ))}
            {(participants || chat.participants)?.length > 3 && (
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-sm">
                +{(participants || chat.participants).length - 3}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        <div className="space-y-4">
          {messages.map((msg, index) => {
            const isCurrentUser = msg.senderId === user?.id;
            const showAvatar = !isCurrentUser && 
              (index === 0 || messages[index - 1].senderId !== msg.senderId);

            return (
              <div
                key={msg.id}
                className={`flex items-start gap-2 ${
                  isCurrentUser ? 'flex-row-reverse' : ''
                }`}
              >
                {showAvatar && !isCurrentUser && (
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={(participants || chat.participants)?.find(p => p.id === msg.senderId)?.avatar} />
                    <AvatarFallback>
                      {(participants || chat.participants)?.find(p => p.id === msg.senderId)?.name[0]}
                    </AvatarFallback>
                  </Avatar>
                )}
                {!showAvatar && !isCurrentUser && <div className="w-8" />}
                <div
                  className={`max-w-[70%] rounded-lg p-3 ${
                    isCurrentUser
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted'
                  }`}
                >
                  {msg.content}
                  <div className="text-xs mt-1 opacity-70">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              </div>
            );
          })}
          {isTyping && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <div className="flex space-x-1">
                <div className="animate-bounce delay-0 h-2 w-2 rounded-full bg-muted-foreground"></div>
                <div className="animate-bounce delay-150 h-2 w-2 rounded-full bg-muted-foreground"></div>
                <div className="animate-bounce delay-300 h-2 w-2 rounded-full bg-muted-foreground"></div>
              </div>
              Someone is typing...
            </div>
          )}
        </div>
      </ScrollArea>

      {/* Message Input */}
      <div className="border-t p-4">
        <form onSubmit={handleSubmit} className="flex items-center gap-2">
          <Button
            type="button"
            size="icon"
            variant="ghost"
            className="rounded-full"
            onClick={() => document.getElementById('file-upload')?.click()}
          >
            <Paperclip className="h-5 w-5" />
            <span className="sr-only">Attach file</span>
            <input
              id="file-upload"
              type="file"
              className="hidden"
              onChange={handleFileSelect}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
              aria-label="Upload attachment"
              title="Upload file attachment"
            />
          </Button>
          <Input
            value={message}
            onChange={handleChange}
            placeholder="Type a message..."
            className="flex-1"
            autoComplete="off"
          />
          <Button type="submit" size="icon" className="rounded-full">
            <Send className="h-5 w-5" />
            <span className="sr-only">Send</span>
          </Button>
        </form>
      </div>
    </Card>
  );
}